"use client";

import { useState, useEffect, useRef } from "react";
import PageContent from "@/components/page-content";
import SectionHeader from "@/components/landing/section-header";
import { Workflow, Copy, Check, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Link from "next/link";

interface BoundingBox {
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
}

const exampleData = {
  image: "/images/solutions/robot-vision/robot_dishes_example.jpg",
  prompt: "dirty dishes",
  boxes: [
    {
      x_min: 0.4222182512910134,
      y_min: 0.5794431174211956,
      x_max: 0.7038814554538547,
      y_max: 0.9063828845338386,
    },
  ],
  response: `{
  "objects": [
    {
      "x_min": 0.422,
      "y_min": 0.579,
      "x_max": 0.704,
      "y_max": 0.906
    }
  ]
}`,
  responseTimeMs: 788,
  inputTokens: 731,
  outputTokens: 28,
};

const codeExamples = {
  python: `import moondream as md
from PIL import Image

# Initialize with API key
model = md.vl(api_key="your-api-key")

# Load your image
image = Image.open("kitchen.jpg")

# Detect objects using natural language
result = model.detect(image, "dirty dishes")
print(result["objects"])`,

  nodejs: `import { vl } from 'moondream';
import fs from 'fs';

// Initialize with API key
const model = new vl({ apiKey: "your-api-key" });

// Load your image
const image = fs.readFileSync("kitchen.jpg");

// Detect objects using natural language
const result = await model.detect({
  image: image,
  object: "dirty dishes"
});
console.log(result.objects);`,

  curl: `curl -X POST https://api.moondream.ai/v1/detect \\
  -H 'Content-Type: application/json' \\
  -H 'X-Moondream-Auth: YOUR_API_KEY' \\
  -d '{
    "image_url": "data:image/jpeg;base64,...",
    "object": "dirty dishes"
  }'`,
};

const ImageWithBoundingBoxes = ({
  image,
  boxes,
}: {
  image: string;
  boxes: BoundingBox[];
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    let isMounted = true;
    const imgElement = new window.Image();

    imgElement.onload = () => {
      if (!isMounted || !ctx || !canvas) return;

      const MAX_DIMENSION = 400;
      let scaledWidth = imgElement.width;
      let scaledHeight = imgElement.height;

      if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
        if (scaledWidth >= scaledHeight) {
          const aspectRatio = scaledHeight / scaledWidth;
          scaledWidth = MAX_DIMENSION;
          scaledHeight = MAX_DIMENSION * aspectRatio;
        } else {
          const aspectRatio = scaledWidth / scaledHeight;
          scaledHeight = MAX_DIMENSION;
          scaledWidth = MAX_DIMENSION * aspectRatio;
        }
      }

      canvas.width = scaledWidth * 2;
      canvas.height = scaledHeight * 2;
      canvas.style.aspectRatio = `${scaledWidth} / ${scaledHeight}`;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);

      ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);

      // Draw bounding boxes
      if (boxes.length > 0) {
        const boxColor = "rgb(34, 197, 94)";
        const transparentColor = "rgba(34, 197, 94, 0.2)";

        boxes.forEach((box) => {
          const x = box.x_min * scaledWidth;
          const y = box.y_min * scaledHeight;
          const width = (box.x_max - box.x_min) * scaledWidth;
          const height = (box.y_max - box.y_min) * scaledHeight;

          ctx.fillStyle = transparentColor;
          ctx.fillRect(x, y, width, height);

          ctx.strokeStyle = boxColor;
          ctx.lineWidth = 3;
          ctx.strokeRect(x, y, width, height);
        });
      }
    };

    imgElement.src = image;

    return () => {
      isMounted = false;
    };
  }, [image, boxes]);

  return <canvas ref={canvasRef} className="rounded-lg border w-full" />;
};

export function HowItWorks() {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(key);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const calculateCost = (inputTokens: number, outputTokens: number): string => {
    const inputCost = (inputTokens / 1_000_000) * 0.3;
    const outputCost = (outputTokens / 1_000_000) * 2.5;
    const totalCost = inputCost + outputCost;
    return `$${totalCost.toFixed(6)}`;
  };

  const playgroundLink = `/playground?image=${encodeURIComponent(exampleData.image)}&prompt=${encodeURIComponent(exampleData.prompt)}&skill=detect`;

  return (
    <PageContent>
      <div className="space-y-6">
        <SectionHeader
          sectionIcon={<Workflow className="size-4" />}
          section="How it works"
          heading="How object detection works"
          description="Describe what you're looking for in natural language and get precise bounding boxes instantly."
        />

        <Card className="overflow-hidden">
          <CardContent className="p-6">
            {/* Image + Prompt/Response Layout */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Image with Bounding Boxes and Prompt Overlay */}
              <div className="lg:w-1/2 shrink-0 relative">
                <ImageWithBoundingBoxes
                  image={exampleData.image}
                  boxes={exampleData.boxes}
                />
                {/* Glassmorphism Prompt Overlay */}
                <div className="absolute top-3 left-3 bg-black/30 backdrop-blur-md rounded-lg px-4 py-2 shadow-lg">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-white/60 mb-0.5">Detect</p>
                  <p className="text-sm font-mono text-white/90">&quot;{exampleData.prompt}&quot;</p>
                </div>
              </div>

              {/* Response */}
              <div className="lg:w-1/2 flex flex-col gap-4">
                <div className="flex-1 flex flex-col min-h-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      Output
                    </h3>
                    <div className="flex items-center gap-4">
                      <div className="text-xs text-muted-foreground whitespace-nowrap">
                        {exampleData.responseTimeMs}ms •{" "}
                        {exampleData.inputTokens + exampleData.outputTokens} tokens •{" "}
                        {calculateCost(exampleData.inputTokens, exampleData.outputTokens)}
                      </div>
                      <Button asChild size="sm">
                        <Link href={playgroundLink}>
                          Try it
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                  <Card className="bg-muted/50 flex-1">
                    <CardContent className="p-4">
                      <pre className="text-sm whitespace-pre-wrap font-mono">
                        {exampleData.response}
                      </pre>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* Code Examples */}
            <div className="mt-6 space-y-2">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Code
              </h3>
              <Tabs defaultValue="python" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="python">Python</TabsTrigger>
                  <TabsTrigger value="nodejs">Node.js</TabsTrigger>
                  <TabsTrigger value="curl">cURL</TabsTrigger>
                </TabsList>

                {(["python", "nodejs", "curl"] as const).map((lang) => {
                  const code = codeExamples[lang];
                  const copyKey = `how-it-works-${lang}`;

                  return (
                    <TabsContent key={lang} value={lang} className="mt-4">
                      <div className="relative">
                        <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm border">
                          <code
                            className={`language-${lang === "nodejs" ? "javascript" : lang === "curl" ? "bash" : lang}`}
                          >
                            {code}
                          </code>
                        </pre>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 h-8 w-8 p-0"
                          onClick={() => copyToClipboard(code, copyKey)}
                        >
                          {copiedCode === copyKey ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContent>
  );
}
